import { formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { useState, ReactNode, useCallback, useReducer } from 'react';
import AssetInput from '../../../../components/AssetInput';
import PercentageSelector from '../../../../components/PercentageSelector';
import { config } from '../../../../config/config';
import {
    EnabledChains,
    OrderBookType,
    OrderDataTypes,
    Tokens,
    Validators,
} from '../../../../config/constants';
import useModalState from '../../../../context/modal/useModalState';
import { formatUSD } from '../../../../helpers';
import { UserPositionRaw } from '../../../../hooks/web3/trade/useUserPositions';
import useTokenDetails from '../../../../hooks/web3/useTokenDetails';
import { preventOverflow } from '../../../../web3/utils';
import { INSUFFICIENT_BALANCE } from '../../../../config/constants';
import { useWeb3React } from '@web3-react/core';
import { PartiallyClosePositionParamsStruct } from '../../../../config/contracts/typechain/ITradeManager';
import LimitPreview from './LimitPreview';
import { BigNumber } from 'ethers';
import { TradePair } from '../../../../context/chart/chartTypes';
import OrderExpiryDropdown from '../../../../components/OrderExpiryDropdown';
import { UserOrder } from '../../../../hooks/web3/trade/useUserOpenOrders';
import { ClosePositionType } from '../ClosePosition';
import useCancelOrder from '../../../../hooks/web3/trade/useCancelOrder';
import useCreateOrder, {
    CreateOrderOptions,
} from '../../../../hooks/web3/trade/useCreateOrder';
import useTokenStats from '../../../../hooks/web3/useTokenStats';

export type ClosePositionLimitProps = {
    children(confirm: {
        confirm: () => Promise<void>;
        validators: Validators[];
    }): ReactNode;
    orderCancelled: boolean;
    setOrderCancelled: React.Dispatch<React.SetStateAction<boolean>>;
};

type LimitPriceProps = {
    selectedTradePair: TradePair | undefined;
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
};

const initialState = {
    limitSize: '',
    limitPrice: '',
};

const PERCENTAGE_MULTIPLIER = 1_000_000;

const ClosePositionLimit: React.FC<ClosePositionLimitProps> = ({
    orderCancelled,
    children,
    setOrderCancelled,
}) => {
    const { chainId } = useWeb3React();
    const { modalData } = useModalState();
    const createOrder = useCreateOrder();

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;
    const userOrder =
        userPosition &&
        'orderData' in userPosition &&
        modalData.data.defaultEditTab === ClosePositionType.LIMIT
            ? userPosition
            : undefined;

    const isPartiallyClosed = userOrder
        ? 'proportion' in userOrder.orderData.params
        : false;

    const partiallyCLoseAmount =
        userOrder && isPartiallyClosed
            ? formatUnits(
                  parseUnits(userOrder.volume, decimals)
                      .mul(
                          (
                              userOrder.orderData as OrderDataTypes<PartiallyClosePositionParamsStruct>
                          ).params.proportion.toString()
                      )
                      .div(PERCENTAGE_MULTIPLIER),
                  decimals
              )
            : '0';

    const [amount, setAmount] = useState<typeof initialState>(
        userOrder?.orderData
            ? {
                  limitSize: isPartiallyClosed ? partiallyCLoseAmount : userOrder.volume,
                  limitPrice: userOrder.limitPrice,
              }
            : initialState
    );
    const [withExpiryDate, toggleWithExpiryDate] = useReducer(
        (prev) => !prev,
        !!userOrder?.orderData.constraints.deadline
    );
    const [expiryDate, setExpiryDate] = useState<Date | undefined>(
        userOrder ? new Date(Number(userOrder.orderData.constraints.deadline)) : undefined
    );

    const cancel = useCancelOrder();

    const validators: Validators[] = [
        {
            errorCondition: parseUnits(amount.limitSize || '0', decimals).isZero(),
            message: 'Close Size is required!',
        },
        {
            errorCondition: parseUnits(amount.limitSize || '0', decimals).gt(
                parseUnits(userPosition?.volume || '0', decimals)
            ),
            message: INSUFFICIENT_BALANCE,
        },
        {
            errorCondition: parseUnits(amount.limitPrice || '0', decimals).isZero(),
            message: 'Limit Price is required!',
        },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setAmount((prev) => ({
            ...prev,
            [name]: preventOverflow(value, decimals),
        }));
    };

    const handleMax = () =>
        setAmount((prev) => ({
            ...prev,
            limitSize: formatUSD(userPosition?.volume || '0', {
                useGrouping: false,
                maxDecimals: 18,
                currency: '',
            }),
        }));

    const confirm = async () => {
        if (!userPosition) throw new Error('UserPosition is undefined');

        if (userOrder && !orderCancelled) {
            await cancel(userOrder.orderId, userOrder.orderType, () =>
                setOrderCancelled(true)
            );
            return;
        }

        const orderOptions: CreateOrderOptions = {
            expiryDate: withExpiryDate ? expiryDate : undefined,
            limitPrice: amount.limitPrice,
            tradePair: userPosition.tradePair,
            userPosition,
        };

        const removedPercentage = parseEther(amount.limitSize || '0')
            .mul(parseEther('1'))
            .div(parseEther(userPosition?.volume || '1'));

        const isFullAmount = removedPercentage.eq(parseEther('1'));

        if (isFullAmount) {
            await createOrder(
                OrderBookType.CLOSE,
                {
                    positionId: userPosition.id,
                    tradePair: userPosition.tradePair.address,
                },
                'ClosePositionOrder',
                userPosition.isShort,
                () => '0',
                orderOptions
            );
        } else {
            await createOrder(
                OrderBookType.PARTIALLY_CLOSE,
                {
                    positionId: userPosition.id,
                    proportion: BigNumber.from(removedPercentage)
                        .mul(PERCENTAGE_MULTIPLIER)
                        .div(parseEther('1'))
                        .toString(),
                    tradePair: userPosition.tradePair.address,
                },
                'PartiallyClosePositionOrder',
                userPosition.isShort,
                () => userPosition.bankruptcyPrice,
                orderOptions
            );
        }
    };

    return (
        <>
            <AssetInput
                label='Size'
                sideLabel={
                    <>
                        Max:{' '}
                        {formatUSD(userPosition?.volume || 0, {
                            maxDecimals: 4,
                            currency: '',
                        })}
                    </>
                }
                name='limitSize'
                value={amount.limitSize}
                onChange={handleChange}
                asset={Tokens.USDC}
                onMax={handleMax}
                disabled={!!userOrder && !orderCancelled}
            />
            <PercentageSelector
                currentValue={amount.limitSize}
                totalValue={userPosition?.volume || '0'}
                handleChange={(value) =>
                    userOrder && !orderCancelled
                        ? undefined
                        : setAmount((prev) => ({
                              ...prev,
                              limitSize: preventOverflow(value, decimals),
                          }))
                }
                disableButtons={!!userOrder && !orderCancelled}
                decimals={decimals}
            />
            <LimitPrice
                selectedTradePair={userPosition?.tradePair}
                value={amount.limitPrice}
                onChange={(value) =>
                    setAmount((prev) => ({
                        ...prev,
                        limitPrice: preventOverflow(value, decimals),
                    }))
                }
                disabled={!!userOrder && !orderCancelled}
            />
            <OrderExpiryDropdown
                value={expiryDate}
                orderExpiryChecked={withExpiryDate}
                onChange={setExpiryDate}
                toggleOrderExpiry={toggleWithExpiryDate}
                disabled={!!userOrder && !orderCancelled}
            />
            <LimitPreview
                amount={amount.limitSize}
                userPosition={userPosition}
                limitPrice={amount.limitPrice}
            />
            {children({ confirm, validators })}
        </>
    );
};

export default ClosePositionLimit;

const LimitPrice: React.FC<LimitPriceProps> = ({
    disabled,
    selectedTradePair,
    value,
    onChange,
}) => {
    const tokenStats = useTokenStats();

    const entryPrice =
        tokenStats && selectedTradePair?.chartSymbol
            ? tokenStats?.[selectedTradePair.chartSymbol]?.price
            : '0';

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            onChange(value);
        },
        [onChange]
    );

    return (
        <AssetInput
            label='Limit Price'
            sideLabel={<>Mark Price: {formatUSD(entryPrice || 0)}</>}
            name='limitPrice'
            value={value}
            onChange={handleChange}
            asset={'USD'}
            onSideLabelClick={() =>
                onChange(
                    formatUSD(preventOverflow(entryPrice || '0'), {
                        currency: '',
                        maxDecimals: 18,
                        useGrouping: false,
                    })
                )
            }
            disabled={disabled}
        />
    );
};
